import {
  parseAuthSessionProjection,
  parseGoogleLoginIntent,
  parseGoogleLoginIntentRequest,
  parsePublicRuntimeConfig,
  parseUpdateProfileRequest,
  type ActiveAuthSessionProjection,
  type AuthSessionProjection,
  type GoogleLoginIntent,
  type GoogleLoginIntentRequest,
  type LeanAuthHttpAdapter,
  type PublicRuntimeConfig,
  type UpdateProfileRequest,
} from '../../server/auth/contracts.js';

export type BrowserFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface BrowserLeanAuthHttpAdapterOptions {
  fetch?: BrowserFetch;
  publicOrigin?: string;
}

function exactPublicOrigin(value: string): string {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error('public origin должен быть exact origin');
  }
  if (parsed.origin !== value) throw new Error('public origin должен быть exact origin');
  return value;
}

function responseError(operation: string, response: Response): Error {
  return new Error(`${operation}: сервер вернул ${response.status}`);
}

async function responseJson(response: Response, operation: string): Promise<unknown> {
  if (response.status !== 200) throw responseError(operation, response);
  const contentType = response.headers.get('content-type')?.split(';', 1)[0]?.trim();
  if (contentType !== 'application/json') {
    throw new Error(`${operation}: сервер вернул не JSON`);
  }
  return response.json();
}

/** Same-origin browser adapter. It never reads or exposes the HttpOnly account bearer. */
export function createBrowserLeanAuthHttpAdapter(
  options: BrowserLeanAuthHttpAdapterOptions = {},
): LeanAuthHttpAdapter {
  const browserFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const publicOrigin = exactPublicOrigin(options.publicOrigin ?? window.location.origin);

  async function jsonRequest(path: string, init: RequestInit, operation: string): Promise<unknown> {
    const response = await browserFetch(path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
    });
    return responseJson(response, operation);
  }

  return {
    async getRuntimeConfig(): Promise<PublicRuntimeConfig> {
      const value = await jsonRequest(
        '/api/runtime-config',
        { method: 'GET', headers: { Accept: 'application/json' } },
        'runtime config',
      );
      return parsePublicRuntimeConfig(value);
    },

    async getSession(): Promise<AuthSessionProjection | null> {
      const response = await browserFetch('/api/auth/session', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (response.status === 401) return null;
      return parseAuthSessionProjection(await responseJson(response, 'auth session'));
    },

    async createGoogleLoginIntent(
      input: GoogleLoginIntentRequest,
    ): Promise<GoogleLoginIntent> {
      const request = parseGoogleLoginIntentRequest(input);
      const value = await jsonRequest(
        '/api/auth/google/intent',
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        },
        'Google login intent',
      );
      const intent = parseGoogleLoginIntent(value, publicOrigin);
      if (intent.returnTo !== request.returnTo) {
        throw new Error('Google login intent: сервер изменил returnTo');
      }
      return intent;
    },

    async updateProfile(
      input: UpdateProfileRequest,
      csrfToken: string,
    ): Promise<ActiveAuthSessionProjection> {
      const request = parseUpdateProfileRequest(input);
      const value = await jsonRequest(
        '/api/users/me',
        {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify(request),
        },
        'profile update',
      );
      const session = parseAuthSessionProjection(value);
      if (session.accountStatus !== 'active') {
        throw new Error('profile update: сервер не активировал профиль');
      }
      return session;
    },

    async logoutCurrent(csrfToken: string): Promise<void> {
      const response = await browserFetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (response.status !== 204) throw responseError('current logout', response);
    },
  };
}
